import { noop } from 'ui-components/default-props/noop';

export const BEGIN_OPTIMISTIC_UPDATE = 'BEGIN_OPTIMISTIC_UPDATE';
export const COMMIT_OPTIMISTIC_UPDATE = 'COMMIT_OPTIMISTIC_UPDATE';
export const REVERT_OPTIMISTIC_UPDATE = 'REVERT_OPTIMISTIC_UPDATE';
export const FLUSH_OPTIMISTIC_UPDATE = 'FLUSH_OPTIMISTIC_UPDATE';

const isCommittingUpdate = (entry, action) => {
  return (
    entry.optimisticId === action.meta.optimisticId &&
    entry.commitType === action.type &&
    action.meta.optimistic === COMMIT_OPTIMISTIC_UPDATE
  );
};

const isRevertingUpdate = (entry, action) => {
  return (
    entry.optimisticId === action.meta.optimisticId &&
    entry.revertType === action.type &&
    action.meta.optimistic === REVERT_OPTIMISTIC_UPDATE
  );
};

const recalculateHistory = (reducer, optimisticHistory, action) => {
  const updatedHistory = optimisticHistory.reduce(
    (acc, entry) => {
      const newEntry = { ...entry };
      if (acc.prevState) {
        // recalc and then pass the recalc forward so any changes after this one are "stacked"
        const recalculatedState = reducer(acc.prevState, entry.action);
        return {
          ...acc,
          newHistory: [
            ...acc.newHistory,
            {
              ...entry,
              action: entry.action,
              state: acc.prevState,
            },
          ],
          prevState: recalculatedState,
        };
      }

      if (isCommittingUpdate(entry, action) || isRevertingUpdate(entry, action)) {
        // we found the entry after which state recalc should happen
        return {
          ...acc,
          prevState: entry.state,
        };
      }

      // if we hit this then we're still looking for the point at which to start
      // recalc'ing state, just push the thing
      return {
        ...acc,
        newHistory: [...acc.newHistory, newEntry],
      };
    },
    { prevState: null, newHistory: [] },
  );
  return updatedHistory.newHistory;
};

export const optimisticUpdater = (reducer, testNotifier = noop, testHistory) => {
  let optimisticHistory = testHistory || [];
  return (state = { ...reducer() }, action) => {
    if (action.meta && action.meta.optimistic) {
      if (action.meta.optimistic === BEGIN_OPTIMISTIC_UPDATE) {
        const { type: commitType } = action.meta.onSuccess();
        const { type: revertType } = action.meta.onError();
        const { optimisticId } = action.meta;
        optimisticHistory.push({
          commitType,
          revertType,
          action,
          state,
          optimisticId,
        });
      }

      if (action.meta.optimistic === COMMIT_OPTIMISTIC_UPDATE) {
        optimisticHistory = recalculateHistory(reducer, optimisticHistory, action);
      }

      if (action.meta.optimistic === REVERT_OPTIMISTIC_UPDATE) {
        const revertEntry = optimisticHistory.find(
          (entry) => entry.optimisticId === action.meta.optimisticId && entry.revertType === action.type,
        ) || { state };
        optimisticHistory = recalculateHistory(reducer, optimisticHistory, action);
        testNotifier(optimisticHistory);
        return reducer(revertEntry.state, action, optimisticHistory);
      }

      if (action.meta.optimistic === FLUSH_OPTIMISTIC_UPDATE) {
        optimisticHistory = optimisticHistory.filter((entry) => entry.optimisticId !== action.meta.optimisticId);
      }
    }
    testNotifier(optimisticHistory);
    return reducer(state, action, optimisticHistory);
  };
};
